'use client'

import { useEffect } from 'react'

const MARKER = 'data-favicon-fallback'

// Next re-renders <head> metadata after hydration, which re-inserts the CDN icon link
// behind a one-shot swap. The observer re-applies the swap; the cap stops a pathological
// ping-pong if something else insists on putting the link back.
const MAX_SWAPS = 20

/**
 * `metadata.icons` bakes NEXT_PUBLIC_FAVICON_URL into <head> at build time, so the CDN
 * copy is what the browser requests first. A <link rel="icon"> that fails to load is
 * silent -- the browser drops back to its own blank page icon with no error and no retry
 * -- so probe the CDN URL here and swap in the bundled public/ copy when it doesn't load.
 *
 * The swap drops the CDN link rather than just appending a second one: with two icon
 * links present, which one the browser keeps is not something to rely on.
 */
export default function FaviconFallback({ localHref }) {
  useEffect(() => {
    const cdnHref = process.env.NEXT_PUBLIC_FAVICON_URL

    if (!cdnHref || cdnHref === localHref) return

    let observer
    let swaps = 0

    const isBrokenIcon = (link) =>
      !link.hasAttribute(MARKER) &&
      (link.href === cdnHref || link.getAttribute('href') === cdnHref)

    const applyFallback = () => {
      if (swaps >= MAX_SWAPS) return

      const broken = [...document.querySelectorAll('link[rel~="icon"]')].filter(isBrokenIcon)
      const missing = !document.querySelector(`link[${MARKER}]`)

      if (!broken.length && !missing) return
      swaps += 1

      broken.forEach((link) => link.remove())

      if (missing) {
        const link = document.createElement('link')
        link.rel = 'icon'
        link.href = localHref
        link.setAttribute(MARKER, '')
        document.head.appendChild(link)
      }
    }

    const probe = new Image()

    probe.onerror = () => {
      try {
        applyFallback()
        observer = new MutationObserver(applyFallback)
        observer.observe(document.head, { childList: true })
      } catch {
        // A favicon is not worth breaking the page over.
      }
    }

    probe.src = cdnHref

    return () => {
      probe.onerror = null
      if (observer) observer.disconnect()
    }
  }, [localHref])

  return null
}
